import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api';
import RightSidebar from './RightSidebar';
import TransactionForm from './TransactionForm'; // We will reuse the form
// MODIFICATION: Import icons for search and download
import { Search, Download } from 'lucide-react';

// MODIFIED: Added new props for filters and CSV download
const TransactionTable = ({ transactions, onAddTransaction, searchTerm, setSearchTerm, fromDate, setFromDate, toDate, setToDate, onDownloadCsv }) => (
  <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
          Vehicle Transactions
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Add and view transactions for all vehicles.
        </p>
      </div>
      <button
        onClick={onAddTransaction}
        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
      >
        Add Transaction
      </button>
    </div>

    {/* NEW: Filter and Action Controls */}
    <div className="flex flex-col sm:flex-row flex-wrap items-center justify-start gap-4 mb-6">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by vehicle no..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
        />
      </div>
      
      {/* Date Filters */}
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <span className="text-gray-500 dark:text-gray-400">to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Download Button */}
      <button
        onClick={onDownloadCsv}
        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
      >
        <Download className="w-5 h-5" />
        <span>Download CSV</span>
      </button>
    </div>

    <div className="overflow-x-auto">
      <table className="min-w-full leading-normal">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-700">
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Vehicle No
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Collection Date
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Amount Received
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Payment Mode
            </th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((txn) => (
            <tr
              key={txn.id}
              className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap font-medium">
                  {txn.vehicle_no}
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap">
                  {new Date(txn.collection_date).toLocaleDateString()}
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-green-600 dark:text-green-400 whitespace-no-wrap font-semibold">
                  ₹{txn.amt_received}
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap">
                  {txn.payment_mode}
                </p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);


const TransactionView = () => {
  const [transactions, setTransactions] = useState([]);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  // NEW: State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await api.get('/transactions');
      // Ensure response.data is an array
      setTransactions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]); // Set to empty array on error
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleTransactionAdded = () => {
    fetchTransactions(); // Refresh the list
    setSidebarOpen(false);
  };

  // NEW: Memoized filtering logic
  const filteredTransactions = useMemo(() => {
    return transactions.filter(txn => {
      const searchLower = searchTerm.toLowerCase();
      
      const searchMatch = (txn.vehicle_no || '').toLowerCase().includes(searchLower);

      // Date filter logic
      if (!fromDate && !toDate) {
        return searchMatch; // No date filter applied
      }

      if (!txn.collection_date) return false; // Don't include items without a date if filtering

      const transactionDate = new Date(txn.collection_date);
      const start = fromDate ? new Date(fromDate) : null;
      const end = toDate ? new Date(toDate) : null;

      // Make date range inclusive
      if (start) start.setUTCHours(0, 0, 0, 0);
      if (end) end.setUTCHours(23, 59, 59, 999);

      const dateMatch = (!start || transactionDate >= start) && (!end || transactionDate <= end);
      
      return searchMatch && dateMatch;
    });
  }, [transactions, searchTerm, fromDate, toDate]);

  // NEW: Handler for CSV Download
  const handleDownloadCsv = () => {
    if (filteredTransactions.length === 0) {
      alert("No data to download.");
      return;
    }

    const headers = ['Vehicle No', 'Collection Date', 'Amount Received', 'Payment Mode'];
    
    const formatCsvCell = (cellData) => {
      const stringData = String(cellData ?? ''); // Handle null/undefined
      if (stringData.includes(',') || stringData.includes('"') || stringData.includes('\n')) {
        return `"${stringData.replace(/"/g, '""')}"`;
      }
      return stringData;
    };

    const csvRows = [
      headers.join(','),
      ...filteredTransactions.map(txn => [
        formatCsvCell(txn.vehicle_no),
        formatCsvCell(new Date(txn.collection_date).toLocaleDateString()),
        formatCsvCell(txn.amt_received),
        formatCsvCell(txn.payment_mode)
      ].join(','))
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="h-full">
      <TransactionTable
        transactions={filteredTransactions} // Pass filtered data to the table
        onAddTransaction={() => setSidebarOpen(true)}
        // Pass new props for filters and actions
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onDownloadCsv={handleDownloadCsv}
      />
      <RightSidebar
        isOpen={isSidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <TransactionForm onTransactionAdded={handleTransactionAdded} />
      </RightSidebar>
    </div>
  );
};

export default TransactionView;
